/**
 * 車輛資訊確認頁面
 * 確認車輛油量和里程，司機可手動修改
 */

import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLiff } from '@/contexts/LiffContext'
import { getDriver } from '@/services/driverService'
import {
  getLastHandover,
  startDuty,
} from '@/services/vehicleService'
import type { Driver, Vehicle, LastHandover } from '@/types'
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation'
import SpeedIcon from '@mui/icons-material/Speed'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

const VehicleConfirmPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { lineUser } = useLiff()

  // 從 location.state 取得選中的車輛
  const selectedVehicle = location.state?.vehicle as Vehicle | undefined

  // ========== State ==========
  const [driver, setDriver] = useState<Driver | null>(null)
  const [lastHandover, setLastHandover] = useState<LastHandover | null>(null)
  const [fuelLevel, setFuelLevel] = useState<string>('')
  const [mileage, setMileage] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ========== 載入資料 ==========
  useEffect(() => {
    const loadData = async () => {
      if (!lineUser) return

      // 檢查是否有選中的車輛
      if (!selectedVehicle) {
        navigate('/vehicle-selection', { replace: true })
        return
      }

      try {
        // 載入司機資料
        const driverData = await getDriver(lineUser.userId)
        if (!driverData) {
          navigate('/binding', { replace: true })
          return
        }
        setDriver(driverData)

        // 載入交班資訊
        const handover = await getLastHandover(selectedVehicle.vehicleId)
        if (handover) {
          setLastHandover(handover)
          setFuelLevel(handover.fuelLevel?.toString() || '0')
          setMileage(handover.mileage?.toString() || '0')
        } else {
          // 沒有歷史資料，使用預設值
          setFuelLevel('0')
          setMileage('0')
        }
      } catch (error) {
        console.error('載入資料失敗:', error)
        setError('載入資料失敗，請稍後再試')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [lineUser, selectedVehicle, navigate])

  // ========== Event Handlers ==========
  // 只有驥達（GIGO）的車要填油量／里程；其餘公司的車不填、帶 0、不檢核（2026-09 客戶決議）
  const fuelMileageRequired = selectedVehicle?.fuelMileageRequired !== false

  const handleConfirm = async () => {
    if (!driver || !selectedVehicle) return

    // 驗證輸入（不需填的車直接帶 0）
    const fuelValue = fuelMileageRequired ? parseFloat(fuelLevel) : 0
    const mileageValue = fuelMileageRequired ? parseFloat(mileage) : 0

    if (fuelMileageRequired && (isNaN(fuelValue) || fuelValue < 0 || fuelValue > 500)) {
      setError('油量必須在 0-500 公升之間')
      return
    }

    if (fuelMileageRequired && (isNaN(mileageValue) || mileageValue < 0)) {
      setError('里程數必須大於等於 0')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      // 接班（建立出勤記錄並設定車輛）
      const result = await startDuty(
        selectedVehicle.vehicleId,
        selectedVehicle.vehicleNo,
        fuelValue,
        mileageValue,
        driver.driverId
      )

      if (result.success) {
        // 導向任務瀏覽頁面，傳遞車輛和司機資料
        navigate('/tasks', {
          state: {
            vehicle: selectedVehicle,
            driver: driver,
            vehicleState: {
              vehicleId: selectedVehicle.vehicleId,
              fuelLevel: fuelValue,
              mileage: mileageValue,
              lastUpdatedAt: new Date().toISOString(),
              lastUpdatedBy: driver.driverId,
              lastUpdatedByName: driver.driverName,
            },
          },
        })
      } else {
        setError(result.message)
      }
    } catch (err) {
      console.error('確認失敗:', err)
      setError('確認失敗，請稍後再試')
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    navigate('/vehicle-selection')
  }

  // ========== 載入中 ==========
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#6B705C' }} />
        <Typography variant="body1" sx={{ mt: 2, color: '#6B705C' }}>
          載入中...
        </Typography>
      </Box>
    )
  }

  // ========== Render ==========
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        py: 3,
      }}
    >
      <Container maxWidth="sm">
        {/* 返回按鈕 */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            mb: 1.5,
            color: '#6B705C',
            '&:hover': {
              backgroundColor: 'rgba(107, 112, 92, 0.08)',
            },
          }}
        >
          返回選擇車輛
        </Button>

        {/* 頁面標題 */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 1.5,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #6B705C 0%, #A5A58D 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DirectionsCarIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                車輛資訊確認
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                {selectedVehicle?.vehicleNo} - {selectedVehicle?.vehicleTypeName}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 錯誤對話框 */}
        <Dialog
          open={!!error}
          onClose={() => setError(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
            <ErrorOutlineIcon />
            錯誤
          </DialogTitle>
          <DialogContent>
            <Typography>{error}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setError(null)} variant="contained" color="primary">
              確定
            </Button>
          </DialogActions>
        </Dialog>

        {/* 上次交班資訊 */}
        {lastHandover && lastHandover.handoverTime && (
          <Paper elevation={2} sx={{ p: 2, mb: 1.5, borderRadius: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
              上次交班記錄
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2">
                交班時間：
                {new Date(lastHandover.handoverTime).toLocaleString('zh-TW', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
              <Typography variant="body2">交班司機：{lastHandover.lastDriver?.driverName || '-'}</Typography>
            </Box>
          </Paper>
        )}

        {/* 車輛狀態輸入表單（非驥達的車不填油量／里程） */}
        {fuelMileageRequired && (
        <Paper elevation={3} sx={{ p: 2, mb: 1.5, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '1.25rem', color: '#6B705C' }}>
            當前車輛狀態
          </Typography>

          {/* 里程 */}
          <TextField
            fullWidth
            label="里程"
            type="number"
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
            disabled={submitting}
            sx={{ mb: 2 }}
            InputLabelProps={{
              sx: { fontSize: '1.5rem', fontWeight: 700 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SpeedIcon sx={{ color: '#6B705C' }} />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">公里</InputAdornment>,
            }}
            inputProps={{
              min: 0,
              step: 1,
            }}
          />

          {/* 油量 */}
          <TextField
            fullWidth
            label="油量"
            type="number"
            value={fuelLevel}
            onChange={(e) => setFuelLevel(e.target.value)}
            disabled={submitting}
            InputLabelProps={{
              sx: { fontSize: '1.5rem', fontWeight: 700 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalGasStationIcon sx={{ color: '#6B705C' }} />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">公升</InputAdornment>,
            }}
            inputProps={{
              min: 0,
              max: 500,
              step: 0.1,
            }}
          />

          <Divider sx={{ my: 2 }} />

          {/* 提示 */}
          <Box
            sx={{
              p: 1.5,
              backgroundColor: '#fff3cd',
              borderRadius: 2,
              border: '1px solid #ffc107',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
              📝 注意事項
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              • 請確認油量與里程數正確無誤
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              • 確認後將開始執行任務
            </Typography>
          </Box>
        </Paper>
        )}

        {/* 底部按鈕的佔位空間 */}
        <Box sx={{ height: 80 }} />
      </Container>

      {/* 接班按鈕 - 固定在底部 */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          backgroundColor: 'rgba(255, 232, 214, 0.95)',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Container maxWidth="sm">
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            onClick={handleConfirm}
            disabled={submitting}
            sx={{
              backgroundColor: '#6B705C',
              color: 'white',
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(107, 112, 92, 0.3)',
              '&:hover': {
                backgroundColor: '#5A5F4A',
                boxShadow: '0 6px 16px rgba(107, 112, 92, 0.4)',
              },
              '&:disabled': {
                backgroundColor: '#A5A58D',
              },
            }}
          >
            {submitting ? '處理中...' : '接班'}
          </Button>
        </Container>
      </Box>
    </Box>
  )
}

export default VehicleConfirmPage
